import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, filter, timeout } from 'rxjs';

import { AuthService } from '@services/auth.service';
import { EmployerService } from '@services/employer.service';
import { PartyService } from '@services/party.service';

import { User } from '@models/user';
import { Party } from '@models/party';
import { environment } from '@envs/environment';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  standalone: false
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {

  public currentUser: User | null = null;
  private passwordChangeModalRef: NgbModalRef | null = null;
  private ssoRegisterModalRef: NgbModalRef | null = null;
  public error = "";
  public hintsShown: boolean = false;
  private authServiceSubscription!: Subscription;
  private employerServiceSubscription!: Subscription;
  private doNotRedirect: boolean = false;

  @ViewChild('changePasswordModal')
  templateRef!: TemplateRef<any>;

  @ViewChild('ssoSignupModal')
  ssoTemplateRef!: TemplateRef<any>;

  constructor(
    private modalService: NgbModal,
    private router: Router,
    private authService: AuthService,
    private employerService: EmployerService,
    private partyService: PartyService,
  ) { }

  async ngAfterViewInit() {
    await this.checkUserSsoRegisterRequired();
    await this.checkUserPasswordChangeRequired();
    this.hintsShown = true;
  }

  ngOnInit(): void {
    this.routeByEmployerParty();
    this.authServiceSubscription = this.authService.getCurrentUser$().subscribe((user: User | null) => {
      this.currentUser = user;
    });
    this.doNotRedirect = false;
  }

  ngOnDestroy(): void {
    if (this.employerServiceSubscription) {
      this.employerServiceSubscription.unsubscribe();
    }
    if (this.authServiceSubscription) {
      this.authServiceSubscription.unsubscribe();
    }
    this.doNotRedirect = true;
  }

  private routeByEmployerParty() {
    this.employerService.getCurrentEmployerParty$().pipe(filter(party => !!party)).pipe(timeout({ first: 4000 })).subscribe(async (employerParty: Party | null) => {
      let employerPartyId = employerParty?.partyId;

      let viewType = await this.partyService.getViewType(employerPartyId!);
      if (!this.doNotRedirect) {
        switch (viewType) {
          case 'CUSTOMER':
            this.router.navigate(['/app/customers/']);
            break;
          case 'SUPPLIER':
            this.router.navigate(['/app/suppliers/']);
            break;
          case 'ADMIN':
            this.router.navigate(['/app/admin/']);
            break;
          default:
            this.authService.logoutAll().then(() => {
              this.router.navigate(['/login/']);
            }).catch(() => {
              this.router.navigate(['/login/']);
            });
            break;
        }
      }
    })
  }

  private checkUserPasswordChangeRequired(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.currentUser?.requirePasswordChange && !this.hintsShown) {
        let options: NgbModalOptions = {
          backdrop: 'static',
          keyboard: false
        }

        if (!this.modalService.hasOpenModals()) {
          return setTimeout(() => {
            this.passwordChangeModalRef = this.modalService.open(this.templateRef, options);
            this.passwordChangeModalRef.result.then(() => {
              return resolve();
            }).catch(() => {
              return resolve();
            });
          })
        }
      }
      return resolve();
    });
  }

  public exitPasswordForm(event: boolean) {
    this.passwordChangeModalRef?.dismiss();
  }

  public checkUserSsoRegisterRequired(): Promise<void> {
    // check if user has to register for sso
    // only show modal when password change modal is not open
    // use user-service to see if user has sso flag set

    if (!environment.sso.enabled) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      return this.partyService.getAttribute(this.currentUser?.partyId!, 'SSO_REMIND_LATER').then((remindLater: string | null) => {
        const lastReminded = new Date(parseInt(remindLater!) || 0);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - lastReminded.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (!this.currentUser?.externalAuthId && !this.hintsShown && (!remindLater || diffDays > 7)) {
          let options: NgbModalOptions = {
            backdrop: 'static',
            keyboard: false,
            size: 'lg'
          }

          if (!this.modalService.hasOpenModals()) {
            return setTimeout(() => {
              this.ssoRegisterModalRef = this.modalService.open(this.ssoTemplateRef, options);
              return this.ssoRegisterModalRef.result.then(() => {
                return resolve();
              }).catch(() => {
                return resolve();
              });
            })
          }
        }

        return resolve();
      }).catch((error: any) => {
        return resolve();
      });
    });
  }

}
